class RecipesCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.jp-recipes__track');
    this.originalCards = Array.from(this.querySelectorAll('.jp-recipes__card'));
    this.dots = Array.from(this.querySelectorAll('.jp-recipes__dot'));
    this.prevBtn = this.querySelector('.jp-recipes__arrow--prev');
    this.nextBtn = this.querySelector('.jp-recipes__arrow--next');

    if (!this.track || !this.originalCards.length) return;

    this.count = this.originalCards.length;

    // Build [clones][real cards][clones] so the carousel can loop seamlessly
    // in either direction with the active card always centered.
    this.originalCards.forEach((card) => {
      const clone = card.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.tabIndex = -1;
      this.track.insertBefore(clone, this.originalCards[0]);
    });
    this.originalCards.forEach((card) => {
      const clone = card.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.tabIndex = -1;
      this.track.appendChild(clone);
    });

    this.cards = Array.from(this.querySelectorAll('.jp-recipes__card'));
    this.activeIndex = this.count + Math.floor(this.count / 2);
    this.dragOffset = 0;
    this.isDragging = false;
    this.dragMoved = false;
    this.startX = 0;
    this.startDragOffset = 0;

    const trackStyle = getComputedStyle(this.track);
    this.activeScale = parseFloat(trackStyle.getPropertyValue('--active-scale')) || 1.286;
    this.gapPx = parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
    this.cardWidth = this.cards[0].getBoundingClientRect().width;

    this.updateActive();
    // Double RAF ensures layout is fully computed before we position the cards
    requestAnimationFrame(() => requestAnimationFrame(() => this.layout(false)));

    this.prevBtn?.addEventListener('click', () => this.go(-1));
    this.nextBtn?.addEventListener('click', () => this.go(1));

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(this.count + i));
    });

    this.track.addEventListener('mousedown', this.onDragStart.bind(this));
    window.addEventListener('mousemove', this.onDragMove.bind(this));
    window.addEventListener('mouseup', this.onDragEnd.bind(this));

    this.track.addEventListener('dragstart', (e) => e.preventDefault());

    this.track.addEventListener('touchstart', this.onDragStart.bind(this), { passive: true });
    this.track.addEventListener('touchmove', this.onDragMove.bind(this), { passive: true });
    this.track.addEventListener('touchend', this.onDragEnd.bind(this));

    this.track.addEventListener('transitionend', this.onTransitionEnd.bind(this));

    window.addEventListener('resize', () => {
      this.cardWidth = this.cards[0].getBoundingClientRect().width;
      this.layout(false);
    });
  }

  go(direction) {
    this.goTo(this.activeIndex + direction);
  }

  goTo(index) {
    this.activeIndex = index;
    this.updateActive();
    this.layout(true);
  }

  updateActive() {
    const realIndex = ((this.activeIndex % this.count) + this.count) % this.count;
    this.cards.forEach((card, i) => card.classList.toggle('is-active', i === this.activeIndex));
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === realIndex));
  }

  setTransitionsEnabled(enabled) {
    this.cards.forEach((card) => {
      card.style.transition = enabled ? '' : 'none';
    });
  }

  // Lays out every card relative to the active one. Each card gets its own
  // translateX + scale, so the active card can be visually larger than its
  // neighbours while the gap between every adjacent pair stays identical —
  // the pitch (center-to-center distance) accounts for each card's own width.
  layout(animate) {
    if (animate) {
      this.setTransitionsEnabled(true);
      this.position(this.dragOffset);
    } else {
      // Apply instantly: disable transitions, move, then flush before re-enabling
      // so the browser never animates the jump.
      this.setTransitionsEnabled(false);
      void this.track.offsetWidth;
      this.position(this.dragOffset);
      void this.track.offsetWidth;
      this.setTransitionsEnabled(true);
    }
  }

  position(extraOffset) {
    const trackCenter = this.track.offsetWidth / 2;
    const W = this.cardWidth;
    const A = W * this.activeScale;
    const G = this.gapPx;
    const regularPitch = W + G;
    const activePitch = (W + A) / 2 + G;

    this.cards.forEach((card, i) => {
      const r = i - this.activeIndex;
      let targetCenter;
      if (r === 0) {
        targetCenter = trackCenter;
      } else if (r > 0) {
        targetCenter = trackCenter + activePitch + (r - 1) * regularPitch;
      } else {
        targetCenter = trackCenter - activePitch - (-r - 1) * regularPitch;
      }
      targetCenter += extraOffset;

      const naturalCenter = card.offsetLeft + card.offsetWidth / 2;
      const deltaX = targetCenter - naturalCenter;
      const scale = i === this.activeIndex ? this.activeScale : 1;
      card.style.transform = `translateX(${deltaX}px) scale(${scale})`;
    });
  }

  // Once the active card finishes sliding onto a cloned position, silently jump
  // back to the matching real card in the middle set — the clone is identical
  // so the jump is invisible.
  onTransitionEnd(e) {
    if (e.propertyName !== 'transform') return;
    if (e.target !== this.cards[this.activeIndex]) return;

    if (this.activeIndex < this.count) {
      this.activeIndex += this.count;
    } else if (this.activeIndex >= this.count * 2) {
      this.activeIndex -= this.count;
    } else {
      return;
    }
    this.updateActive();
    this.layout(false);
  }

  snapToNearest() {
    const pitch = this.cardWidth + this.gapPx;
    const shift = Math.round(-this.dragOffset / pitch);
    this.dragOffset = 0;
    this.goTo(this.activeIndex + shift);
  }

  getClientX(e) {
    return e.touches ? e.touches[0].clientX : e.clientX;
  }

  onDragStart(e) {
    if (e.button !== undefined && e.button !== 0) return;
    this.isDragging = true;
    this.dragMoved = false;
    this.startX = this.getClientX(e);
    this.startDragOffset = this.dragOffset;
    this.track.classList.add('is-dragging');
    this.setTransitionsEnabled(false);
  }

  onDragMove(e) {
    if (!this.isDragging) return;
    const delta = this.getClientX(e) - this.startX;
    if (Math.abs(delta) > 5) this.dragMoved = true;
    this.dragOffset = this.startDragOffset + delta;
    this.position(this.dragOffset);
  }

  onDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.classList.remove('is-dragging');
    this.setTransitionsEnabled(true);
    if (this.dragMoved) {
      this.snapToNearest();
    } else {
      this.dragOffset = 0;
      this.layout(true);
    }
  }
}

customElements.define('recipes-carousel', RecipesCarousel);
